import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Alert, InputAdornment, IconButton,
  Typography, Box, Paper, Container, Slide
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../css/Login.css';
import '../css/FormLayout.css'; // même layout que Register
import API_BASE_URL from '../api';

const Login = ({ setIsAuthenticated }) => {
  const [formData, setFormData] = useState({
    emailAddress: '',
    password: '',
    verificationCode: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('authToken')) {
      navigate('/ajouter-cv');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.emailAddress.trim() || !formData.password.trim()) {
    setError('Veuillez saisir un email et un mot de passe.');
    return;
  }

  try {
    const res = await axios.post(`${API_BASE_URL}/api/users/login`, {
      emailAddress: formData.emailAddress.toLowerCase(),
      password: formData.password
    });

    const {
      token,
      userId,
      userName,
      expiresIn,
      arrive,
      usercompanyName,
      usercompanyId,
      userType
    } = res.data;

    // Enregistrement local
    localStorage.setItem('authToken', token);
    localStorage.setItem('userId', userId);
    localStorage.setItem('userName', userName);
    localStorage.setItem('arrive', arrive);
    localStorage.setItem('companyName', usercompanyName);
    localStorage.setItem('companyId', usercompanyId);
    localStorage.setItem('userType', userType);
    localStorage.setItem('tokenExpiration', new Date().getTime() + expiresIn * 1000);

    setSuccess('Connexion réussie');
    setError('');
    setIsAuthenticated(true);
    setTimeout(() => navigate('/ajouter-cv'), 1000);

  } catch (err) {
    if (err.response?.data?.message === 'Account not verified') {
      setShowVerificationInput(true);
      setShowResendButton(true);
    }

    // Gestion du compte désactivé
    if (err.response?.status === 403 && err.response?.data?.message === 'Compte désactivé. Veuillez contacter un administrateur.') {
      setError('🚫 Votre compte a été désactivé. Contactez un administrateur.');
    } else {
      setError(err.response?.data?.message || 'Erreur de connexion');
    }
  }
};


  const handleResendVerification = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/users/resend-verification`, {
        emailAddress: formData.emailAddress
      });
      setSuccess('Code renvoyé. Vérifiez votre email.');
      setError('');
    } catch {
      setError("Erreur lors de l'envoi du code");
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE_URL}/api/users/verify`, {
        emailAddress: formData.emailAddress.toLowerCase(),
        verificationCode: formData.verificationCode
      });

      setSuccess('Compte vérifié. Vous pouvez vous connecter.');
      setShowVerificationInput(false);
      setShowResendButton(false);
    } catch {
      setError('Code de vérification invalide');
    }
  };

  return (
    <Box className="layout-wrapper">
      <Box className="info-section-layout">
        <Box className="info-content">
          <Typography variant="h5" className="info-title">
            Bienvenue sur ParseIT
          </Typography>
          <Typography variant="body1" className="info-subtitle">
            Connectez-vous à votre espace pour gérer vos CV et vos activités
          </Typography>
          <ul className="info-points">
            <li>✓ Suivi de vos dossiers et parsings</li>
            <li>✓ Historique de vos actions</li>
            <li>✓ Accès sécurisé à la plateforme</li>
          </ul>
        </Box>
      </Box>

      <Box className="form-section">
        <Container maxWidth="sm">
          <Slide in direction="up" timeout={500}>
            <Paper className="form-paper" elevation={6}>
              <Typography variant="h5" align="center" gutterBottom>
                Connexion
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

              <form onSubmit={handleSubmit}>
                <TextField
                  name="emailAddress"
                  label="Email"
                  value={formData.emailAddress}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                />
                <TextField
                  name="password"
                  label="Mot de passe"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={!formData.emailAddress.trim() || !formData.password.trim()}
                >
                  Se connecter
                </Button>
              </form>

              {showResendButton && (
                <>
                  <Button
                    variant="text"
                    onClick={handleResendVerification}
                    sx={{ mt: 2 }}
                  >
                    Renvoyer le code de vérification
                  </Button>

                  {showVerificationInput && (
                    <form onSubmit={handleVerify}>
                      <TextField
                        name="verificationCode"
                        label="Code de vérification"
                        value={formData.verificationCode}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                      />
                      <Button type="submit" fullWidth variant="outlined">
                        Vérifier le compte
                      </Button>
                    </form>
                  )}
                </>
              )}

              <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                Vous n'avez pas de compte ? <a href="/register">Créer votre compte</a>
              </Typography>
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                <a href="/reset-password">Mot de passe oublié ?</a>
              </Typography>
            </Paper>
          </Slide>
        </Container>
      </Box>
    </Box>
  );
};

export default Login;
